import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, message, Input, Select, Tabs, Popconfirm, InputNumber, Modal, Form } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Option } = Select;

const CLASSES = ['A', 'B', 'C', 'D'];
const CLASSE_DESC = { A: 'Inflamáveis, tóxicos ≤20ppm, H₂, acetileno', B: 'Combustíveis <200°C, tóxicos >20ppm', C: 'Vapor, gases asfixiantes, ar comprimido', D: 'Demais fluidos' };

export default function ClassificacaoNR13() {
  const [classificacoes, setClassificacoes] = useState([]);
  const [fluidos, setFluidos] = useState([]);
  const [editando, setEditando] = useState({});
  const [modalFluido, setModalFluido] = useState(false);
  const [editFluido, setEditFluido] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    const [r1, r2] = await Promise.all([
      api.get('/classificacao-nr13'),
      api.get('/classificacao-nr13/fluidos'),
    ]);
    setClassificacoes(r1.data);
    setFluidos(r2.data);
  };

  const salvarPrazo = async (id) => {
    try {
      await api.put('/classificacao-nr13/' + id, editando[id]);
      message.success('Prazo atualizado!');
      setEditando(prev => { const n = {...prev}; delete n[id]; return n; });
      carregar();
    } catch { message.error('Erro ao salvar'); }
  };

  const salvarFluido = async (values) => {
    try {
      if (editFluido?.id) {
        await api.put('/classificacao-nr13/fluidos/' + editFluido.id, values);
      } else {
        await api.post('/classificacao-nr13/fluidos', values);
      }
      message.success('Fluido salvo!');
      setModalFluido(false);
      setEditFluido(null);
      form.resetFields();
      carregar();
    } catch { message.error('Erro ao salvar fluido'); }
  };

  const colunasClassificacao = [
    { title: 'Classe', dataIndex: 'classe', width: 80, render: v => <Tag color={{ A: 'red', B: 'orange', C: 'blue', D: 'green' }[v]}>{v}</Tag> },
    { title: 'Descrição da Classe', width: 300, render: (_, r) => <span style={{ fontSize: 12, color: '#666' }}>{CLASSE_DESC[r.classe]}</span> },
    { title: 'Grupo', dataIndex: 'grupo', width: 70, render: v => <Tag>G{v}</Tag> },
    { title: 'Faixa P.V.', dataIndex: 'descricao_grupo', width: 150, render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    { title: 'Categoria', dataIndex: 'categoria', width: 90, render: v => <Tag color="purple">{v}</Tag> },
    { title: 'Prazo Externo (anos)', width: 160, render: (_, r) => (
      editando[r.id] ? 
        <InputNumber min={1} max={10} value={editando[r.id].prazo_externo} onChange={v => setEditando(prev => ({...prev, [r.id]: {...prev[r.id], prazo_externo: v}}))} /> :
        <span>{r.prazo_externo} {r.prazo_externo === 1 ? 'ano' : 'anos'}</span>
    )},
    { title: 'Prazo Interno (anos)', width: 160, render: (_, r) => (
      editando[r.id] ?
        <InputNumber min={1} max={15} value={editando[r.id].prazo_interno} onChange={v => setEditando(prev => ({...prev, [r.id]: {...prev[r.id], prazo_interno: v}}))} /> :
        <span>{r.prazo_interno} {r.prazo_interno === 1 ? 'ano' : 'anos'}</span>
    )},
    { title: 'Ações', width: 120, render: (_, r) => (
      editando[r.id] ?
        <Button size="small" type="primary" icon={<SaveOutlined />} onClick={() => salvarPrazo(r.id)}>Salvar</Button> :
        <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(prev => ({...prev, [r.id]: { prazo_externo: r.prazo_externo, prazo_interno: r.prazo_interno }}))}>Editar</Button>
    )},
  ];

  const colunasFluidos = [
    { title: 'Fluido', dataIndex: 'nome' },
    { title: 'Classe Sugerida', dataIndex: 'classe', width: 130, render: v => <Tag color={{ A: 'red', B: 'orange', C: 'blue', D: 'green' }[v]}>{v} — {CLASSE_DESC[v]?.split(',')[0]}</Tag> },
    { title: 'Densidade', dataIndex: 'densidade', width: 130,
      render: (v) => (v === null || v === undefined
        ? <Tag color="orange">não se aplica</Tag>
        : String(v).replace('.', ',')) },
    { title: 'Ações', width: 120, render: (_, r) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => { setEditFluido(r); form.setFieldsValue(r); setModalFluido(true); }} />
        <Popconfirm title="Remover fluido?" onConfirm={async () => { await api.delete('/classificacao-nr13/fluidos/' + r.id); carregar(); }}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Classificação NR-13</h2>
        <p style={{ color: '#888', margin: 0 }}>Configure fluidos e prazos de inspeção conforme NR-13</p>
      </div>

      <Tabs items={[
        {
          key: 'classificacoes',
          label: 'Tabela de Classificação (20 combinações)',
          children: (
            <Card size="small">
              <p style={{ color: '#888', marginBottom: 12, fontSize: 12 }}>
                Edite os prazos de inspeção para cada combinação Classe × Grupo. A Categoria é calculada automaticamente pela norma.
              </p>
              <Table columns={colunasClassificacao} dataSource={classificacoes} rowKey="id" size="small" pagination={false} />
            </Card>
          )
        },
        {
          key: 'fluidos',
          label: 'Lista de Fluidos',
          children: (
            <Card size="small" extra={
              <Button type="primary" icon={<PlusOutlined />} size="small" onClick={() => { setEditFluido(null); form.resetFields(); setModalFluido(true); }}>
                Novo Fluido
              </Button>
            }>
              <p style={{ color: '#888', marginBottom: 12, fontSize: 12 }}>
                Lista de fluidos disponíveis no formulário do laudo com a classe sugerida pela NR-13.
              </p>
              <Table columns={colunasFluidos} dataSource={fluidos} rowKey="id" size="small" pagination={false} />
            </Card>
          )
        },
      ]} />

      <Modal title={editFluido ? 'Editar Fluido' : 'Novo Fluido'} open={modalFluido} onCancel={() => { setModalFluido(false); setEditFluido(null); form.resetFields(); }} footer={null}>
        <Form form={form} layout="vertical" onFinish={salvarFluido}>
          <Form.Item name="nome" label="Nome do Fluido" rules={[{ required: true }]}>
            <Input placeholder="Ex: Ar comprimido" />
          </Form.Item>
          <Form.Item name="densidade"
            label="Densidade relativa à água (apenas líquidos)"
            extra="Usada no cálculo do costado de tanques. Deixe vazio para gases.">
            <InputNumber style={{ width: '100%' }} min={0} max={30} step={0.01}
              decimalSeparator="," placeholder="ex.: 0,85" />
          </Form.Item>
          <Form.Item name="classe" label="Classe Sugerida (NR-13)" rules={[{ required: true }]}>
            <Select>
              {CLASSES.map(c => <Option key={c} value={c}>{c} — {CLASSE_DESC[c]}</Option>)}
            </Select>
          </Form.Item>
          <Button type="primary" htmlType="submit" block>Salvar</Button>
        </Form>
      </Modal>
    </div>
  );
}
